"use client";

import type { PlainDate } from "@sa-nu/shared-utils/date";
import { createContext, useContext, useState, type ReactNode } from "react";

import { useRoomTypeReserveContext } from "../../room-type-reservation/_context/room-type-reserve-context";
import { computePendingStayRange } from "../../room-type-reservation/_utils/compute-pending-stay-range";

// firstNight/lastNight を discriminated union にし、「真に日程未選択」を today への
// フォールバックではなく null/null で表現する。片方だけ null にはならないため、
// 呼び出し側は firstNight の null チェックだけで lastNight も一緒に絞り込める。
export type PendingStaySelectionContextValue =
  | { firstNight: PlainDate; lastNight: PlainDate }
  | { firstNight: null; lastNight: null };

const PendingStaySelectionContext =
  createContext<PendingStaySelectionContextValue | null>(null);

/**
 * 拠点詳細ドロワーを開いている間だけ有効な一時的な日程選択状態。
 * 確定済みの検索条件（`RoomTypeReserveContext` の firstNight/lastNight）とは独立に持ち、
 * ドロワーが閉じれば（アンマウントされれば）破棄される。
 */
export function PendingStaySelectionProvider({
  selectedNight,
  children,
}: {
  /**
   * カレンダーセルタップでの選択日、または確定済み検索条件があればその日程。
   * どちらも無い「真に日程未選択」の状態は null で表現する（today への
   * フォールバックにすると、拠点一覧の日程フィルタなし＝範囲判定スキップという
   * 挙動と食い違い、今日1泊だけ空きが無い拠点が誤って満室表示になる）。
   */
  selectedNight: PlainDate | null;
  children: ReactNode;
}) {
  const reserveCtx = useRoomTypeReserveContext();
  // mount 時の1回だけ初期化する。以降 reserveCtx.firstNight/lastNight が変化しても
  // 追従させない（一時選択状態を確定済み検索条件から独立させるのがこの Context の目的）。
  const [value] = useState<PendingStaySelectionContextValue>(() => {
    if (selectedNight === null) {
      return { firstNight: null, lastNight: null };
    }
    return computePendingStayRange({
      selectedNight,
      existingFirstNight: reserveCtx?.firstNight ?? null,
      existingLastNight: reserveCtx?.lastNight ?? null,
    });
  });

  return (
    <PendingStaySelectionContext.Provider value={value}>
      {children}
    </PendingStaySelectionContext.Provider>
  );
}

export function usePendingStaySelectionContext(): PendingStaySelectionContextValue {
  const ctx = useContext(PendingStaySelectionContext);
  if (ctx === null) {
    throw new Error(
      "usePendingStaySelectionContext must be used within PendingStaySelectionProvider",
    );
  }
  return ctx;
}

/**
 * throw しない版。`RoomTypeRow` のように `SiteCard`（Provider の外）と
 * `SiteDetailBody`（Provider の内側）の両経路からレンダリングされるコンポーネント用。
 */
export function useOptionalPendingStaySelectionContext(): PendingStaySelectionContextValue | null {
  return useContext(PendingStaySelectionContext);
}
